/**
 * JustOnlyOneThing — Google Analytics 4 (GA4) Universal Event Tracking Engine
 * Provides automated site-wide behavioral telemetry, engagement heartbeats,
 * scroll depth milestones, outbound clicks, downloads, copies, errors, and rich calculator/tool events.
 */

(function () {
  'use strict';

  // 1. GA4 Configuration
  window.GA_MEASUREMENT_ID = window.GA_MEASUREMENT_ID || 'G-JBP64SWY0B';

  // Ensure dataLayer & gtag function exist safely even if offline / ad-blocked
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }

  // Debug mode helper (enabled on localhost, 127.0.0.1, or via query param / localStorage)
  const isDebug =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.search.includes('debug_ga=true') ||
    localStorage.getItem('ga_debug') === 'true';

  function logDebug(eventName, params) {
    if (!isDebug) return;
    try {
      console.log(
        `%c[GA4 Telemetry]%c ${eventName}`,
        'background:#0284c7;color:#ffffff;font-weight:700;padding:2px 6px;border-radius:3px 0 0 3px;',
        'background:#0f172a;color:#38bdf8;font-weight:600;padding:2px 8px;border-radius:0 3px 3px 0;',
        params
      );
    } catch (_) {}
  }

  /**
   * Universal Event Dispatcher
   * @param {string} eventName - GA4 event identifier
   * @param {Object} [params] - Key-value event parameters
   */
  function gtagEvent(eventName, params = {}) {
    if (!eventName) return;

    // Detect active tool/page name
    const pathname = window.location.pathname.replace(/\/$/, '') || '/';
    let toolName = 'homepage';
    if (pathname.includes('/services/')) {
      toolName = pathname.split('/services/')[1]?.split('/')[0] || 'services_index';
    } else if (pathname.includes('/blog/')) {
      toolName = pathname.split('/blog/')[1]?.split('/')[0] || 'blog_index';
    } else if (pathname.includes('/about')) {
      toolName = 'about';
    } else if (pathname.includes('/site-map')) {
      toolName = 'sitemap';
    } else if (pathname.includes('/authors/')) {
      toolName = 'author_profile';
    } else if (pathname.includes('404')) {
      toolName = '404_not_found';
    }

    const enrichedParams = {
      page_path: window.location.pathname,
      page_title: document.title,
      tool_name: toolName,
      theme: document.documentElement.dataset.theme || 'dark',
      country: (typeof currentCountry !== 'undefined' && currentCountry?.id) || undefined,
      ...params,
    };

    // Clean undefined values
    Object.keys(enrichedParams).forEach((k) => {
      if (enrichedParams[k] === undefined) delete enrichedParams[k];
    });

    try {
      window.gtag('event', eventName, enrichedParams);
      logDebug(eventName, enrichedParams);
    } catch (e) {
      if (isDebug) console.warn('[GA4] Event dispatch failed:', e);
    }
  }

  // Expose globally for app usage
  window.gtagEvent = gtagEvent;
  window.Analytics = {
    track: gtagEvent,
    trackToolAction: (tool, action, details = {}) => {
      gtagEvent('tool_action', { tool_name: tool, action, ...details });
    },
    trackCalculator: (tool, primaryMetric, primaryValue, extra = {}) => {
      gtagEvent('calculator_calculate', {
        tool_name: tool,
        primary_metric: primaryMetric,
        primary_value: primaryValue,
        ...extra,
      });
    },
    trackDownload: (fileName, fileType, tool) => {
      gtagEvent('file_download', {
        file_name: fileName,
        file_type: fileType,
        tool_name: tool,
      });
    },
    trackCopy: (contentType, tool, charLength) => {
      gtagEvent('clipboard_copy', {
        content_type: contentType,
        tool_name: tool,
        char_length: charLength,
      });
    },
    trackError: (tool, errorType, errorMessage) => {
      gtagEvent('validation_error', {
        tool_name: tool,
        error_type: errorType,
        error_message: String(errorMessage).slice(0, 150),
      });
    },
  };

  /* ─────────────────────────────────────────────────────────────────
   * AUTOMATED TELEMETRY HOOKS
   * ───────────────────────────────────────────────────────────────── */

  // 1. Scroll Depth Milestones (25%, 50%, 75%, 90%, 100%)
  const scrollMilestones = [25, 50, 75, 90, 100];
  const trackedMilestones = new Set();

  function checkScrollDepth() {
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;

    const scrolledPct = Math.round((window.scrollY / docHeight) * 100);

    for (const milestone of scrollMilestones) {
      if (scrolledPct >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);
        gtagEvent('scroll_depth', {
          percent: milestone,
          page_path: window.location.pathname,
        });
      }
    }
  }

  window.addEventListener('scroll', checkScrollDepth, { passive: true });

  // 2. Active User Engagement Heartbeat (15s, 30s, 60s, 120s, 300s, 600s)
  const heartbeatIntervals = [15, 30, 60, 120, 300, 600];
  let activeSeconds = 0;
  let isTabActive = !document.hidden;

  document.addEventListener('visibilitychange', () => {
    isTabActive = !document.hidden;
  });

  const heartbeatTimer = setInterval(() => {
    if (isTabActive) {
      activeSeconds += 1;
      if (heartbeatIntervals.includes(activeSeconds)) {
        gtagEvent('user_engagement_heartbeat', {
          duration_seconds: activeSeconds,
          page_path: window.location.pathname,
        });
      }
    }
    if (activeSeconds > 600) {
      clearInterval(heartbeatTimer);
    }
  }, 1000);

  // 3. Outbound Link & Social Link Click Tracker
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link || !link.href) return;

    try {
      const url = new URL(link.href, window.location.href);
      const isExternal = url.hostname && url.hostname !== window.location.hostname;

      if (isExternal && !url.protocol.startsWith('javascript:')) {
        gtagEvent('outbound_click', {
          link_url: link.href,
          link_domain: url.hostname,
          link_text: (link.textContent || '').trim().slice(0, 60),
          outbound: true,
        });
      }
    } catch (_) {}
  });

  // 4. File Downloads (CSV, JSON, PDF, TXT)
  document.addEventListener('click', (e) => {
    const target = e.target.closest('a[download], button[id*="export"], button[id*="download"], [data-action="download"]');
    if (!target) return;

    const downloadAttr = target.getAttribute('download');
    const id = target.id || '';
    const text = (target.textContent || '').trim();

    let ext = 'file';
    let name = downloadAttr || id || text;

    if (name.includes('.csv') || text.toLowerCase().includes('csv')) ext = 'csv';
    else if (name.includes('.json') || text.toLowerCase().includes('json')) ext = 'json';
    else if (name.includes('.pdf') || text.toLowerCase().includes('pdf')) ext = 'pdf';
    else if (name.includes('.txt') || text.toLowerCase().includes('txt')) ext = 'txt';

    gtagEvent('file_download', {
      file_name: name,
      file_type: ext,
      button_id: id || undefined,
    });
  });

  // 5. Universal Copy to Clipboard Clicks
  document.addEventListener('click', (e) => {
    const copyBtn = e.target.closest('button[id*="copy"], button[class*="copy"], [data-action="copy"], .copy-btn');
    if (!copyBtn) return;

    const id = copyBtn.id || '';
    const text = (copyBtn.textContent || copyBtn.title || '').trim();
    const contentType = copyBtn.dataset.contentType || id.replace(/copy/i, '') || text || 'clipboard_data';

    gtagEvent('clipboard_copy', {
      content_type: contentType.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      button_id: id || undefined,
    });
  });

  // 6. Tool Card & Directory Click Interception
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.tool-card, .gsearch-item, .service-card');
    if (!card) return;

    const title =
      card.querySelector('h2, .tool-card-title, .gsearch-item-title')?.textContent?.trim() ||
      card.getAttribute('title') ||
      '';
    const href = card.getAttribute('href') || card.querySelector('a')?.getAttribute('href') || '';
    const category = card.closest('[id]')?.id || card.querySelector('.gsearch-item-cat')?.textContent?.trim() || '';

    if (title || href) {
      gtagEvent('select_tool', {
        tool_title: title,
        tool_url: href,
        tool_category: category,
        source_page: window.location.pathname,
      });
    }
  });

  // 7. Category Filter Tabs Click Interception
  document.addEventListener('click', (e) => {
    const filterBtn = e.target.closest('.filter-tab, .cat-tab, .catalog-filter-btn');
    if (!filterBtn) return;

    const cat = filterBtn.dataset.filter || filterBtn.dataset.category || filterBtn.textContent.trim();
    gtagEvent('filter_category', {
      category: cat.toLowerCase(),
      source_page: window.location.pathname,
    });
  });

  // 8. Sliders & Range Inputs (Debounced 500ms)
  const sliderDebounceTimers = {};

  document.addEventListener('input', (e) => {
    const target = e.target;
    if (target.type !== 'range') return;

    const sliderId = target.id || target.name || 'unnamed_slider';
    clearTimeout(sliderDebounceTimers[sliderId]);

    sliderDebounceTimers[sliderId] = setTimeout(() => {
      gtagEvent('slider_change', {
        slider_id: sliderId,
        slider_value: Number(target.value) || target.value,
        slider_min: Number(target.min) || undefined,
        slider_max: Number(target.max) || undefined,
        slider_step: Number(target.step) || undefined,
      });
    }, 500);
  });

  // 9. Global and Section Search Listeners
  function initSearchTracking() {
    const searchInputs = [
      { id: 'globalSearchInput', type: 'global' },
      { id: 'financeSearchInput', type: 'finance_section' },
      { id: 'developerSearchInput', type: 'developer_section' },
      { id: 'sitemapSearchInput', type: 'sitemap' },
    ];

    searchInputs.forEach(({ id, type }) => {
      const el = document.getElementById(id);
      if (!el) return;

      let debounceTimer = null;
      el.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        const term = e.target.value.trim();
        if (term.length < 2) return;

        debounceTimer = setTimeout(() => {
          gtagEvent('site_search', {
            search_term: term,
            search_type: type,
          });
        }, 600);
      });
    });
  }

  // 10. JavaScript Errors & Unhandled Promise Rejections
  window.addEventListener('error', (e) => {
    gtagEvent('exception', {
      description: `${e.message} at ${e.filename || 'unknown'}:${e.lineno || 0}`,
      fatal: false,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    gtagEvent('exception', {
      description: `UnhandledRejection: ${e.reason ? (e.reason.message || String(e.reason)) : 'unknown'}`,
      fatal: false,
    });
  });

  // 11. Print Event Listener
  window.addEventListener('beforeprint', () => {
    gtagEvent('print_page', {
      page_path: window.location.pathname,
    });
  });

  // DOM ready initializations
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchTracking);
  } else {
    initSearchTracking();
  }
})();
